import { Asset } from '@/api/generatedApi';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { AssetPageObjectModel } from '@/poms/assetPageObjectModel';
import { Box, styled, SxProps, Theme } from '@mui/material';
import { Fragment } from 'react';
import { AddToCompareButton } from '../CompareCart/AddToCompareButton';
import { FindMatchesButton } from '../Matches/FindMatchesButton';
import { MoveAssetButton } from './MoveAssetButton';
import { DeleteAssetButton } from './DeleteAssetButton';
import { DownloadAssetButton } from './DownloadAssetButton';
import { OverflowActionMenu } from './OverflowActionMenu';
import { ReplaceAssetFileButton } from './ReplaceAssetFileButton';
import { ReprocessAssetButton } from './ReprocessAssetButton';
import { ReprocessCriticalGeometryButton } from './ReprocessCriticalGeometryButton';
import { ViewAssetButton } from './ViewAssetButton';

const StyledAssetActionRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  padding: '2px',
  borderBottom: 'none',
  width: '100%',
  gap: theme.spacing(0.5),
}));

const READ_ONLY_ACTION_KEYS = new Set(['compare', 'download']);

export const AssetActionRow = ({
  asset,
  hideAssetPageLink = false,
  showReprocess = false,
  showMove = false,
  readOnly = false,
  collapseExtraActions = false,
  onDelete,
  onReprocessCriticalGeometry,
  isReprocessCriticalGeometryDisabled = false,
  slim = false,
  sx,
}: {
  asset?: Asset;
  hideAssetPageLink?: boolean;
  showReprocess?: boolean;
  showMove?: boolean;
  readOnly?: boolean;
  collapseExtraActions?: boolean;
  onDelete?: () => void;
  onReprocessCriticalGeometry?: () => void | Promise<void>;
  isReprocessCriticalGeometryDisabled?: boolean;
  slim?: boolean;
  sx?: SxProps<Theme>;
}) => {
  const { currentUserIsAtleastTenantViewer, permissions } = useGetCurrentUser();
  const shouldShowActions = currentUserIsAtleastTenantViewer(asset?.tenantId);

  const iconStyles: SxProps<Theme> = slim
    ? {
        '& .MuiIconButton-root': {
          padding: '2px',
          '& .MuiSvgIcon-root': {
            fontSize: '1rem',
          },
        },
      }
    : {};

  if (!shouldShowActions || !asset) {
    return;
  }

  const hasCollapsibleActions =
    Boolean(onReprocessCriticalGeometry) ||
    (showReprocess && permissions.canReprocessAssets) ||
    permissions.canUploadAssets ||
    (showMove && permissions.canCreateFolders);

  const collapse = collapseExtraActions && hasCollapsibleActions;
  const extraVariant = collapse ? 'button' : 'icon';

  const actions = [
    { key: 'view', node: !hideAssetPageLink && <ViewAssetButton asset={asset} />, isExtra: false },
    { key: 'findMatches', node: <FindMatchesButton asset={asset} variant='icon' />, isExtra: false },
    { key: 'compare', node: <AddToCompareButton asset={asset} variant='icon' />, isExtra: false },
    {
      key: 'criticalGeometry',
      node: (
        <ReprocessCriticalGeometryButton
          disabled={isReprocessCriticalGeometryDisabled}
          variant={extraVariant}
          onReprocess={onReprocessCriticalGeometry}
        />
      ),
      isExtra: true,
    },
    {
      key: 'reprocess',
      node: showReprocess && <ReprocessAssetButton asset={asset} variant={extraVariant} />,
      isExtra: true,
    },
    { key: 'replace', node: <ReplaceAssetFileButton asset={asset} variant={extraVariant} />, isExtra: true },
    { key: 'download', node: <DownloadAssetButton asset={asset} variant='icon' />, isExtra: false },
    {
      key: 'move',
      node: showMove && <MoveAssetButton asset={asset} variant={extraVariant} />,
      isExtra: true,
    },
    { key: 'delete', node: <DeleteAssetButton asset={asset} variant='icon' onDelete={onDelete} />, isExtra: false },
  ].filter(action => Boolean(action.node) && !(readOnly && !READ_ONLY_ACTION_KEYS.has(action.key)));

  const inlineActions = collapse ? actions.filter(action => !action.isExtra) : actions;
  const collapsedActions = collapse ? actions.filter(action => action.isExtra) : [];

  return (
    <StyledAssetActionRow data-testid={AssetPageObjectModel.AssetActionRow} sx={{ ...iconStyles, ...sx }}>
      {inlineActions.map(action => (
        <Fragment key={action.key}>{action.node}</Fragment>
      ))}
      {collapsedActions.length > 0 && (
        <OverflowActionMenu>
          {collapsedActions.map(action => (
            <Fragment key={action.key}>{action.node}</Fragment>
          ))}
        </OverflowActionMenu>
      )}
    </StyledAssetActionRow>
  );
};
